import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import ErrorNotice from "../../errors/ErrorNotice";
import { formatDateTime } from "../../shared/formatting/dateTimes";
import { useLoad } from "../../shared/loading/useLoad";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import Chip from "../../ui/feedback/Chip";
import EmptyState from "../../ui/feedback/EmptyState";
import LoadStatus from "../../ui/feedback/LoadStatus";
import StatusMessage from "../../ui/feedback/StatusMessage";
import ConfirmAction from "../../ui/overlays/ConfirmAction";
import { FORM_DIVIDED_LIST_CLASS, FORM_SECTION_HEADING_CLASS } from "../../ui/styles/formStyles";
import BudgetFields from "./BudgetFields";
import KeyForm from "./KeyForm";
import ProviderSpendSection from "./ProviderSpendSection";
import { usageText } from "./usageText";
import { budgetOf, changeKeyBudget, readServerKeys, revokeServerKey, type ServerHost, type ServerKey } from "./serverKeysApi";

/**
 * Server keys (account administrators; ADR-0023, MA-C4, built provisionally): the
 * installation's API keys, write-only, each with its monthly budget and DMW's count of its
 * use; who has a "DMW server" because a project names a group of theirs that administrators
 * approved; and the
 * providers' own spend, where an admin key is set up (monitoring only).
 */
export default function ServerKeysPage() {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => readServerKeys(signal), []);
  const { state, reload } = useLoad(load);

  return (
    <div className="app-card w-full max-w-3xl space-y-6 p-6 shadow-sm">
      <div className="space-y-1">
        <h1 className="app-title text-xl font-semibold">{t("serverKeys.title")}</h1>
        <p className="app-text-muted text-sm">{t("serverKeys.intro")}</p>
      </div>
      <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />
      {state.status === "loaded" ? (
        <>
          {!state.value.available ? (
            <StatusMessage tone="info" title={t("serverKeys.offTitle")}>
              <p>{t("serverKeys.offBody", { reason: state.value.offReason ?? "" })}</p>
            </StatusMessage>
          ) : null}
          <section className="space-y-3">
            <h2 className={FORM_SECTION_HEADING_CLASS}>{t("serverKeys.keysTitle")}</h2>
            {state.value.keys.length === 0 ? <EmptyState>{t("serverKeys.noKeys")}</EmptyState> : null}
            <ul className={FORM_DIVIDED_LIST_CLASS}>
              {state.value.keys.map((key) => (
                <KeyRow key={key.keyId} serverKey={key} onChanged={reload} />
              ))}
            </ul>
            {state.value.available ? <KeyForm onAdded={reload} /> : null}
          </section>
          <section className="space-y-3">
            <h2 className={FORM_SECTION_HEADING_CLASS}>{t("serverKeys.hostsTitle")}</h2>
            <p className="app-text-muted text-sm">{t("serverKeys.hostsHint")}</p>
            {state.value.hosts.length === 0 ? <EmptyState>{t("serverKeys.noHosts")}</EmptyState> : null}
            <ul className={FORM_DIVIDED_LIST_CLASS}>
              {state.value.hosts.map((host) => (
                <HostRow key={host.userId} host={host} />
              ))}
            </ul>
          </section>
          <ProviderSpendSection />
        </>
      ) : null}
    </div>
  );
}

function KeyRow({ serverKey, onChanged }: { serverKey: ServerKey; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const { state, submit } = useSubmission();
  const [editing, setEditing] = useState<{ calls: string; tokens: string } | null>(null);
  const [revoking, setRevoking] = useState(false);
  const revoked = serverKey.state === "revoked";
  return (
    <li className="space-y-2 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">
            {serverKey.routeId} <span className="app-text-muted font-normal">· {serverKey.api}</span>
          </p>
          <p className="app-text-muted truncate text-xs">
            {t("serverKeys.keyDetails", {
              hint: serverKey.hint,
              models: t("serverKeys.llmCount", { count: serverKey.models.length }),
              time: formatDateTime(serverKey.addedAt, i18n.language),
            })}
          </p>
          <p className="text-xs">{usageText(serverKey.usage, { calls: serverKey.budgetCalls, tokens: serverKey.budgetTokens }, t)}</p>
          {revoked ? <Chip className="mt-1">{t("serverKeys.revoked")}</Chip> : null}
        </div>
        {!revoked ? (
          <div className="flex shrink-0 gap-2">
            <AppButton size="sm" disabled={state.status === "submitting"}
              onClick={() => { setEditing({ calls: serverKey.budgetCalls?.toString() ?? "", tokens: serverKey.budgetTokens?.toString() ?? "" }); }}>
              {t("serverKeys.changeBudget")}
            </AppButton>
            <AppButton size="sm" variant="danger" disabled={state.status === "submitting"} onClick={() => { setRevoking(true); }}>
              {t("serverKeys.revokeKey")}
            </AppButton>
          </div>
        ) : null}
      </div>
      {editing !== null ? (
        <form className="space-y-2" onSubmit={(event) => {
          event.preventDefault();
          submit(async () => {
            await changeKeyBudget(serverKey.keyId, budgetOf(editing));
            setEditing(null);
            onChanged();
          });
        }}>
          <BudgetFields value={editing} onChange={setEditing} />
          <div className="flex gap-2">
            <AppButton type="submit" size="sm" variant="primary" disabled={state.status === "submitting"}>{t("serverKeys.saveBudget")}</AppButton>
            <AppButton size="sm" onClick={() => { setEditing(null); }}>{t("common.cancel")}</AppButton>
          </div>
        </form>
      ) : null}
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      {revoking ? (
        <ConfirmAction
          title={t("serverKeys.revokeKey")}
          confirmLabel={t("serverKeys.revokeKey")}
          tone="danger"
          onConfirm={async () => {
            await revokeServerKey(serverKey.keyId);
            onChanged();
          }}
          onClose={() => { setRevoking(false); }}
        >
          <p>{t("serverKeys.revokeKeyConfirm")}</p>
        </ConfirmAction>
      ) : null}
    </li>
  );
}

function HostRow({ host }: { host: ServerHost }) {
  const { t, i18n } = useTranslation();
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
      <span className="min-w-0 truncate">{host.userName ?? t("serverKeys.removedAccount")}</span>
      <span className="app-text-muted text-xs">{t("serverKeys.pairedAt", { time: formatDateTime(host.pairedAt, i18n.language) })}</span>
      <Chip>{host.online ? t("serverKeys.hostOnline") : t("serverKeys.hostOffline")}</Chip>
    </li>
  );
}
